import { sameOriginRedirectPath } from './safeRedirect'
import { markEmailConfirmed } from './authReturn'
import { identifyUser, track } from './analytics'
import { trackingParams } from './rsvpLink'
import { pendingRsvpTarget } from './pendingRsvp'
import { supabase } from './supabase'

// ON-407: shared by /auth/confirm and /auth/callback, after a successful
// verification. Where a web signup goes, in order:
//
// 1. user_metadata.pending_rsvp (written by the web signUp) → the event page
//    with ?rsvp=confirm, rebuilt from a validated UUID (see pendingRsvp.ts).
//    This is the live path: the "Confirm signup" template is fixed as
//    {{ .SiteURL }}/auth/confirm?token_hash=..&type=signup — no redirect_to.
// 2. a same-origin redirect_to, if a template ever carries one (absolute
//    same-origin URLs only; not the bare SITE_URL app signups default to,
//    not /auth/*).
// 3. null → the caller keeps its /confirm-email app hand-off unchanged.
//
// Recovery always goes to /reset-password; email_change keeps the app hand-off.
const FOLLOWS_REDIRECT = ['signup', 'magiclink']

const EVENT_PATH = /^\/share\/event\/([^/?#]+)/

interface VerifiedUser {
  id?: string
  user_metadata?: Record<string, unknown>
}

/**
 * Best-effort removal of pending_rsvp once it has been acted on (or found
 * stale), so a later verification can't replay it. Never blocks navigation:
 * on failure the 14-day expiry still bounds it.
 */
function clearPendingRsvp() {
  try {
    void Promise.resolve(supabase.auth.updateUser({ data: { pending_rsvp: null } })).catch(() => {})
  } catch {
    /* ignore */
  }
}

function confirmedSignup(userId: string | undefined, props: Record<string, unknown>) {
  // Picked up by the event page: registration_completed + confirm prompt.
  markEmailConfirmed()
  if (userId) identifyUser(userId)
  track('auth_link_opened', { ...props, link_type: 'signup' })
}

export function webRedirectTarget(
  type: string | null,
  rawRedirect: string | null,
  user: VerifiedUser | null | undefined,
): string | null {
  if (!type || !FOLLOWS_REDIRECT.includes(type)) return null

  if (type === 'signup') {
    const meta = user?.user_metadata
    if (meta?.pending_rsvp != null) {
      clearPendingRsvp()
      const pending = pendingRsvpTarget(meta)
      if (pending) {
        confirmedSignup(user?.id, {
          event_id: pending.eventId,
          ...(pending.ref ? { ref: pending.ref } : {}),
          ...(pending.src ? { src: pending.src } : {}),
        })
        return pending.path
      }
    }
  }

  const target = sameOriginRedirectPath(rawRedirect, window.location.origin)
  if (!target) return null
  if (type === 'signup') {
    const url = new URL(target, window.location.origin)
    const eventId = EVENT_PATH.exec(url.pathname)?.[1]
    confirmedSignup(user?.id, { ...(eventId ? { event_id: eventId } : {}), ...trackingParams(url.search) })
  }
  return target
}
